// A school's rules for arrivals, pickups and the bus (docs/spec/school-rules.md): what each step
// asks for, set by the school, some from a grade up. Shared by the Worker (checks and defaults), the
// console (the Rules page), the staff app and the door tablet.

export const GRADES = ["PK", "K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"] as const;
export type Grade = (typeof GRADES)[number];
export const isGrade = (g: unknown): g is Grade => typeof g === "string" && (GRADES as readonly string[]).includes(g);
/** A grade as schools write it ("Kindergarten", "KG", "Pre-K", "TK", "3rd", "Grade 03", "gr 5") as a Grade, or null. */
export function normaliseGrade(v: string | null | undefined): Grade | null {
  const w = (v ?? "").trim().toLowerCase().replace(/[.\s_-]+/g, " ").replace(/^(grade|gr|year|yr|level)\s*/, "").trim();
  if (!w) return null;
  if (/^(pk|pre ?k|prek|pre kindergarten|prekindergarten|tk|transitional kindergarten|pre school|preschool|nursery)$/.test(w)) return "PK";
  if (/^(k|kg|kinder|kindergarten|kindergarden|0|00)$/.test(w)) return "K";
  const n = w.match(/^0?(\d{1,2})(st|nd|rd|th)?( grade)?$/)?.[1];
  return n && Number(n) >= 1 && Number(n) <= 12 ? (String(Number(n)) as Grade) : null;
}

export const gradeLabel = (g: Grade) => (g === "PK" ? "Pre-K" : g === "K" ? "Kindergarten" : `Grade ${g}`);

/**
 * Whether a child of `grade` is at or above `from`. `from` null: nobody. An unknown grade is never
 * old enough: a relaxed rule never applies to a child the school has not placed in a grade.
 */
export function fromGrade(grade: Grade | null | undefined, from: Grade | null | undefined): boolean {
  if (!from || !grade) return false;
  return GRADES.indexOf(grade) >= GRADES.indexOf(from);
}

export interface SchoolRules {
  /** The class teacher may mark a child who came in on their own as here, from this grade (null: never). */
  walk_in_from: Grade | null;
  /** Children may leave the bus at their stop without an adult, from this grade; parents can override either way with a reason. */
  bus_walk_from: Grade | null;
  /** A pickup's signature: required, or the adult may skip it (recorded as signature_skipped). */
  pickup_signature: "required" | "optional";
  /** A pickup's photo with a face: required, or the pickup goes ahead without one (recorded as no_face). */
  pickup_photo: "required" | "optional";
  /** Children may go home on their own (walk, bike) from this grade, with their parent's consent (null: never). */
  home_alone_from: Grade | null;
  /** An older brother or sister at the school may collect a younger one, with the parent's consent, from this grade (null: never). */
  sibling_from: Grade | null;
  /** Parents' same-day bus changes ("not on the bus today") go straight to the driver until this time ("13:00"); later ones go to the office too (null: no cut-off). */
  bus_changes_close: string | null;
  /** A drop-off after this time ("08:30") is an arrival late (flagged late_arrival); null: not tracked. */
  late_after: string | null;
}

export const DEFAULT_RULES: SchoolRules = {
  walk_in_from: "3", bus_walk_from: "3", pickup_signature: "required", pickup_photo: "required",
  home_alone_from: "5", sibling_from: null, bus_changes_close: "13:00", late_after: null,
};

/** Consents a parent gives for a child (or the office from a paper form): going home alone, leaving with a sibling. */
export type ConsentKind = "home_alone" | "sibling";
